import React, { useCallback, useEffect, useState } from 'react';

// Pagination Component
const Pagination = ({ currentPage, totalPages, handlePageChange }) => {
  // Determine the range of pages to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxDisplayedPages = 5; // Maximum number of page buttons to display
    let startPage = Math.max(1, currentPage - Math.floor(maxDisplayedPages / 2));
    let endPage = startPage + maxDisplayedPages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxDisplayedPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return { pageNumbers, startPage, endPage };
  };

  const { pageNumbers, startPage, endPage } = getPageNumbers();

  return (
    <div className="pagination">
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </button>

      {startPage > 1 && (
        <>
          <button onClick={() => handlePageChange(1)}>1</button>
          {startPage > 2 && <span>...</span>}
        </>
      )}

      {pageNumbers.map((number) => (
        <button
          key={number}
          onClick={() => handlePageChange(number)}
          className={currentPage === number ? 'active' : ''}
        >
          {number}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span>...</span>}
          <button onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </>
      )}

      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </button>
    </div>
  );
};

const ManageAuditTrails = ({ backendUrl, resetKey }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter inputs (what the user is typing)
  const [emailFilter, setEmailFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [detailsFilter, setDetailsFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  // Applied filters (what is actually sent to the server)
  const [appliedFilters, setAppliedFilters] = useState({});
  const [eventTypes, setEventTypes] = useState([]); // distinct types for the dropdown
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const itemsPerPage = 50; // Number of logs per page

  // Report view (full, un-paginated result of the current filters)
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportLogs, setReportLogs] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportTruncated, setReportTruncated] = useState(false);
  const [reportUniqueIps, setReportUniqueIps] = useState([]);
  const [reportUniqueUsers, setReportUniqueUsers] = useState([]);
  const [reportAnonIpCount, setReportAnonIpCount] = useState(0);

  const fetchLogs = useCallback(async () => {
    const token = localStorage.getItem('auth-token');
    if (!token) return;

    const params = new URLSearchParams();
    params.set('page', currentPage);
    params.set('limit', itemsPerPage);
    if (appliedFilters.email) params.set('email', appliedFilters.email);
    if (appliedFilters.eventType) params.set('eventType', appliedFilters.eventType);
    if (appliedFilters.details) params.set('details', appliedFilters.details);
    if (appliedFilters.startDate) params.set('startDate', appliedFilters.startDate);
    if (appliedFilters.endDate) params.set('endDate', appliedFilters.endDate);

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/audit/auditlogs?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setLogs(result.logs);
        setTotalPages(result.pages || 1);
        setTotalLogs(result.total || 0);
        setSelectedLogs(new Set()); // Clear selected logs when re-fetching
      } else {
        console.error('Error fetching logs:', result.message);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, currentPage, appliedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, resetKey]);

  // Load the distinct event types once to populate the filter dropdown.
  useEffect(() => {
    const fetchEventTypes = async () => {
      const token = localStorage.getItem('auth-token');
      if (!token) return;
      try {
        const response = await fetch(`${backendUrl}/audit/event-types`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-token': token },
        });
        const result = await response.json();
        if (result.success) setEventTypes(result.eventTypes || []);
      } catch (error) {
        console.error('Error fetching event types:', error);
      }
    };
    fetchEventTypes();
  }, [backendUrl, resetKey]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      email: emailFilter.trim(),
      eventType: eventTypeFilter.trim(),
      details: detailsFilter.trim(),
      startDate: startDateFilter,
      endDate: endDateFilter,
    });
    setCurrentPage(1); // Reset to first page after filtering
  };

  const handleClearFilters = () => {
    setEmailFilter("");
    setEventTypeFilter("");
    setDetailsFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
    setAppliedFilters({});
    setCurrentPage(1); // Reset to first page after clearing filters
  };

  // Open the report: fetch the FULL filtered set (no pagination).
  // Uses the CURRENT filter inputs (so the user doesn't have to click Apply first)
  // and syncs the paginated table to the same filters.
  const handleGenerateReport = async () => {
    const token = localStorage.getItem('auth-token');
    if (!token) return;

    const filters = {
      email: emailFilter.trim(),
      eventType: eventTypeFilter.trim(),
      details: detailsFilter.trim(),
      startDate: startDateFilter,
      endDate: endDateFilter,
    };
    // Keep the table in sync with what the report shows.
    setAppliedFilters(filters);
    setCurrentPage(1);

    const params = new URLSearchParams();
    if (filters.email) params.set('email', filters.email);
    if (filters.eventType) params.set('eventType', filters.eventType);
    if (filters.details) params.set('details', filters.details);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    setReportOpen(true);
    setReportLoading(true);
    try {
      const response = await fetch(`${backendUrl}/audit/report?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) {
        setReportLogs(result.logs || []);
        setReportTotal(result.total || 0);
        setReportTruncated(result.truncated || false);
        setReportUniqueIps(result.uniqueIps || []);
        setReportUniqueUsers(result.uniqueUsers || []);
        setReportAnonIpCount(result.anonymousIpCount || 0);
      } else {
        console.error('Error generating report:', result.message);
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Quote a CSV cell (wrap in quotes, escape inner quotes).
  const csvCell = (value) => {
    const s = value === undefined || value === null ? "" : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleExportCsv = () => {
    const header = ["Email", "Event Type", "Event Time", "Details", "IP Address", "Browser"];
    const rows = reportLogs.map((log) => [
      log.email || "",
      log.eventType || "",
      new Date(log.eventTime).toLocaleString(),
      formatDetails(log.details),
      log.ipAddress || "",
      log.userAgent ? getBrowserName(log.userAgent) : "",
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    // Prepend BOM so Excel opens UTF-8 correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Print / Save as PDF: render a clean, self-contained HTML document in a new
  // window and print it there. This avoids inheriting the app's (dark) styles
  // and gives full control over the print layout, which @media print on the live
  // modal does not.
  const htmlEscape = (s) =>
    String(s === undefined || s === null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const handlePrintReport = () => {
    const rowsHtml = reportLogs
      .map((log) => {
        const outcome =
          log.details?.outcome && log.details.outcome !== "success"
            ? `<span class="denied">[${htmlEscape(log.details.outcome)}]</span> `
            : "";
        return `<tr>
          <td>${htmlEscape(log.email || "N/A")}</td>
          <td>${htmlEscape(log.eventType)}</td>
          <td>${htmlEscape(new Date(log.eventTime).toLocaleString())}</td>
          <td class="details">${outcome}${htmlEscape(formatDetails(log.details))}</td>
          <td>${htmlEscape(log.ipAddress || "")}</td>
          <td>${htmlEscape(getBrowserName(log.userAgent))}</td>
        </tr>`;
      })
      .join("");

    const chipsHtml = reportCountsByType()
      .map(([type, count]) => `<span class="chip">${htmlEscape(type)}: <b>${count}</b></span>`)
      .join("");

    const truncatedNote = reportTruncated
      ? ` (showing first ${reportLogs.length} — refine filters to see the rest)`
      : "";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Audit Trail Report</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #222; margin: 24px; }
    h1 { font-size: 18px; margin: 0 0 6px; }
    .meta { font-size: 12px; color: #444; margin: 2px 0; }
    .chips { margin: 12px 0 16px; }
    .chip { display: inline-block; background: #eef2f7; border: 1px solid #d6dee8;
            border-radius: 12px; padding: 2px 8px; font-size: 11px; margin: 0 6px 6px 0; }
    .chip-ip { background: #dbeafe; border-color: #93c5fd; }
    .chip-user { background: #dcfce7; border-color: #86efac; }
    .chip-anon { background: #f3f4f6; border-color: #d1d5db; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th, td { text-align: left; padding: 5px 6px; border-bottom: 1px solid #ddd; vertical-align: top; }
    th { border-bottom: 2px solid #999; background: #f4f6f9; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    td.details { max-width: 340px; word-break: break-word; }
    .denied { color: #dc3545; font-weight: bold; }
    @page { margin: 16mm 12mm; }
  </style>
</head>
<body>
  <h1>Audit Trail Report</h1>
  <div class="meta"><b>Generated:</b> ${htmlEscape(new Date().toLocaleString())}</div>
  <div class="meta"><b>Filters:</b> ${htmlEscape(reportFilterSummary())}</div>
  <div class="meta"><b>Total matching:</b> ${reportTotal}${htmlEscape(truncatedNote)}</div>
  <div class="chips"><span class="chip chip-ip">Unique IPs: <b>${reportUniqueIps.length}</b></span><span class="chip chip-user">Unique Users: <b>${reportUniqueUsers.length}</b></span>${reportAnonIpCount > 0 ? `<span class="chip chip-anon">Anonymous: from <b>${reportAnonIpCount}</b> IP${reportAnonIpCount === 1 ? "" : "s"}</span>` : ""}${chipsHtml}</div>
  <table>
    <thead>
      <tr><th>Email</th><th>Event Type</th><th>Event Time</th><th>Details</th><th>IP Address</th><th>Browser</th></tr>
    </thead>
    <tbody>${rowsHtml || '<tr><td colspan="6">No logs found for these filters.</td></tr>'}</tbody>
  </table>
  <script>window.onload = function () { window.print(); };</script>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (!w) {
      alert("Please allow pop-ups to print/save the report as PDF.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Human-readable description of the filters applied to this report.
  const reportFilterSummary = () => {
    const f = appliedFilters;
    const parts = [];
    if (f.email) parts.push(`Email contains "${f.email}"`);
    if (f.eventType) parts.push(`Event type = ${f.eventType}`);
    if (f.details) parts.push(`Details contains "${f.details}"`);
    if (f.startDate) parts.push(`From ${f.startDate}`);
    if (f.endDate) parts.push(`To ${f.endDate}`);
    return parts.length ? parts.join(" · ") : "No filters (all events)";
  };

  // Counts per event type for the report summary.
  const reportCountsByType = () => {
    const counts = {};
    reportLogs.forEach((log) => {
      counts[log.eventType] = (counts[log.eventType] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  };

  const toggleLogSelection = (logId) => {
    setSelectedLogs((prevSelected) => {
      const updatedSelection = new Set(prevSelected);
      if (updatedSelection.has(logId)) {
        updatedSelection.delete(logId);
      } else {
        updatedSelection.add(logId);
      }
      return updatedSelection;
    });
  };

  const handleDeleteSelectedLogs = async () => {
    if (selectedLogs.size === 0) {
      alert("No logs selected for deletion.");
      return;
    }

    const confirmDeletion = window.confirm("Are you sure you want to delete the selected logs?");
    if (!confirmDeletion) return;

    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/audit/auditlogs/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ logIds: Array.from(selectedLogs) }),
      });

      const result = await response.json();
      if (result.success) {
        alert("Selected logs deleted successfully.");
        fetchLogs(); // Refresh the logs after deletion
      } else {
        console.error('Error deleting logs:', result.message);
        alert(`Error deleting logs: ${result.message}`);
      }
    } catch (error) {
      console.error('Error deleting logs:', error);
      alert("An error occurred while deleting logs.");
    }
  };

  // Build a human-readable summary of a log's details object.
  // Shows readable fields (names, emails, titles, reasons...) for ANY event type,
  // and hides raw ObjectId references which aren't useful at a glance.
  const isObjectId = (v) => typeof v === "string" && /^[a-f0-9]{24}$/i.test(v);
  const humanizeKey = (key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
  const formatDetails = (details) => {
    if (!details || typeof details !== "object") return "N/A";
    const parts = [];
    for (const [key, value] of Object.entries(details)) {
      if (key === "outcome") continue; // shown separately
      if (value === undefined || value === null || value === "") continue;
      if (isObjectId(value)) continue; // raw id reference, skip
      if (Array.isArray(value)) {
        const readable = value.filter((v) => !isObjectId(v));
        if (readable.length === 0) continue;
        parts.push(`${humanizeKey(key)}: ${readable.join(", ")}`);
      } else {
        parts.push(`${humanizeKey(key)}: ${value}`);
      }
    }
    return parts.length ? parts.join(" · ") : "N/A";
  };

  // Helper function to extract the browser name from the user agent
  const getBrowserName = (userAgent) => {
    if (!userAgent) return "Unknown";
    if (userAgent.includes("Chrome") && !userAgent.includes("Edge") && !userAgent.includes("OPR")) return "Chrome";
    if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) return "Safari";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Edge")) return "Edge";
    if (userAgent.includes("OPR") || userAgent.includes("Opera")) return "Opera";
    if (userAgent.includes("MSIE") || userAgent.includes("Trident")) return "Internet Explorer";
    return "Unknown";
  };

  const handlePageChange = (pageNumber) => {
    // Ensure the new page number is within bounds
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-group">
      <h2>Audit Trails</h2>

      <div className="filters">
        <input
          type="text"
          placeholder="Filter by Email"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
        />
        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
        >
          <option value="">All Event Types</option>
          {eventTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search in details (e.g. product)"
          value={detailsFilter}
          onChange={(e) => setDetailsFilter(e.target.value)}
        />
        <input
          type="date"
          placeholder="Start Date"
          value={startDateFilter}
          onChange={(e) => setStartDateFilter(e.target.value)}
        />
        <input
          type="date"
          placeholder="End Date"
          value={endDateFilter}
          onChange={(e) => setEndDateFilter(e.target.value)}
        />
        <button onClick={handleApplyFilters}>Apply Filters</button>
        <button onClick={handleClearFilters}>Clear Filters</button>
        <button
          style={{ backgroundColor: '#dc3545' }}
          onClick={handleDeleteSelectedLogs}
          disabled={selectedLogs.size === 0}
        >
          Delete Selected ({selectedLogs.size})
        </button>
        <button
          style={{ backgroundColor: '#0d6efd' }}
          onClick={handleGenerateReport}
        >
          View Report
        </button>
      </div>
{/* Top Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      )}
      <table>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={(e) =>
                  setSelectedLogs(
                    new Set(e.target.checked ? logs.map(log => log._id) : [])
                  )
                }
                checked={
                  logs.length > 0 &&
                  logs.every(log => selectedLogs.has(log._id))
                }
              />
            </th>
            <th>Email</th>
            <th>Event Type</th>
            <th>Event Time</th>
            <th>Details</th>
            <th>IP Address</th>
            <th>Browser</th>
          </tr>
        </thead>
        <tbody>
          {logs.length > 0 ? (
            logs.map((log) => (
              <tr key={log._id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedLogs.has(log._id)}
                    onChange={() => toggleLogSelection(log._id)}
                  />
                </td>
                <td>{log.email || "N/A"}</td>
                <td>{log.eventType}</td>
                <td>{new Date(log.eventTime).toLocaleString()}</td>
                <td title={log.details ? JSON.stringify(log.details, null, 2) : ""}>
                  {log.details?.outcome && log.details.outcome !== "success" && (
                    <strong style={{ color: "#dc3545", marginRight: 6 }}>
                      [{log.details.outcome}]
                    </strong>
                  )}
                  {formatDetails(log.details)}
                </td>
                <td>{log.ipAddress}</td>
                <td>{getBrowserName(log.userAgent)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">No logs found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Bottom Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      )}

      {/* Optional: Display current page and total logs */}
      <div className="page-info">
        Page {currentPage} of {totalPages} | Total Logs: {totalLogs}
      </div>

      {reportOpen && (
        <div
          className="audit-report-overlay"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", justifyContent: "center", alignItems: "flex-start",
            padding: "24px", zIndex: 1000, overflow: "auto",
          }}
          onClick={() => setReportOpen(false)}
        >
          <div
            className="audit-report-modal"
            style={{
              background: "#fff", color: "#222", borderRadius: 8,
              width: "min(1100px, 100%)", padding: "24px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Toolbar (not printed) */}
            <div
              className="audit-report-no-print"
              style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 16 }}
            >
              <button onClick={handleExportCsv} disabled={reportLoading || reportLogs.length === 0}>
                Export CSV
              </button>
              <button onClick={handlePrintReport} disabled={reportLoading || reportLogs.length === 0}>
                Print / Save PDF
              </button>
              <button style={{ backgroundColor: "#6c757d" }} onClick={() => setReportOpen(false)}>
                Close
              </button>
            </div>

            {/* Report header */}
            <h2 style={{ color: "#222", marginTop: 0 }}>Audit Trail Report</h2>
            <p style={{ margin: "4px 0", fontSize: 13 }}>
              <strong>Generated:</strong> {new Date().toLocaleString()}
            </p>
            <p style={{ margin: "4px 0", fontSize: 13 }}>
              <strong>Filters:</strong> {reportFilterSummary()}
            </p>
            <p style={{ margin: "4px 0", fontSize: 13 }}>
              <strong>Total matching:</strong> {reportTotal}
              {reportTruncated && ` (showing first ${reportLogs.length} — refine filters to see the rest)`}
            </p>

            {reportLoading ? (
              <p>Loading report…</p>
            ) : (
              <>
                {/* Summary by event type */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "12px 0 16px" }}>
                  <span
                    style={{
                      background: "#dbeafe", border: "1px solid #93c5fd",
                      borderRadius: 12, padding: "2px 10px", fontSize: 12,
                    }}
                  >
                    Unique IPs: <strong>{reportUniqueIps.length}</strong>
                  </span>
                  <span
                    style={{
                      background: "#dcfce7", border: "1px solid #86efac",
                      borderRadius: 12, padding: "2px 10px", fontSize: 12,
                    }}
                  >
                    Unique Users: <strong>{reportUniqueUsers.length}</strong>
                  </span>
                  {reportAnonIpCount > 0 && (
                    <span
                      style={{
                        background: "#f3f4f6", border: "1px solid #d1d5db",
                        borderRadius: 12, padding: "2px 10px", fontSize: 12,
                      }}
                    >
                      Anonymous: from <strong>{reportAnonIpCount}</strong> IP{reportAnonIpCount === 1 ? "" : "s"}
                    </span>
                  )}
                  {reportCountsByType().map(([type, count]) => (
                    <span
                      key={type}
                      style={{
                        background: "#eef2f7", border: "1px solid #d6dee8",
                        borderRadius: 12, padding: "2px 10px", fontSize: 12,
                      }}
                    >
                      {type}: <strong>{count}</strong>
                    </span>
                  ))}
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Email", "Event Type", "Event Time", "Details", "IP Address", "Browser"].map((h) => (
                        <th key={h} style={{ textAlign: "left", borderBottom: "2px solid #ccc", padding: "6px 8px" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportLogs.length > 0 ? (
                      reportLogs.map((log) => (
                        <tr key={log._id} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "5px 8px" }}>{log.email || "N/A"}</td>
                          <td style={{ padding: "5px 8px" }}>{log.eventType}</td>
                          <td style={{ padding: "5px 8px" }}>{new Date(log.eventTime).toLocaleString()}</td>
                          <td style={{ padding: "5px 8px" }}>
                            {log.details?.outcome && log.details.outcome !== "success" && (
                              <strong style={{ color: "#dc3545", marginRight: 6 }}>[{log.details.outcome}]</strong>
                            )}
                            {formatDetails(log.details)}
                          </td>
                          <td style={{ padding: "5px 8px" }}>{log.ipAddress}</td>
                          <td style={{ padding: "5px 8px" }}>{getBrowserName(log.userAgent)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" style={{ padding: "8px" }}>No logs found for these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAuditTrails;
